// @ts-nocheck

import type { RootState } from 'client/js/redux';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';
import { connect } from 'react-redux';
import { BranchLoader, LineChart, MessageBox } from '.';
import AnalyticsNoDataMessage from '../../../main/components/AnalyticsNoDataMessage';
import { getCurrentAppLocalCurrency } from '../../../main/selectors/AppSelectors';
import { isToday, isYesterday } from '../../../main/support/dateUtils';
import type { AxesParameters } from '../../../main/types/timeseriesTypes';
import { DATA_LIMIT_MESSAGE, ERROR_WHITELIST } from '../../support/analyticsConstants';
import {
  constructMultiAxesConfig,
  determineTickFormat,
  flattenAllGroupByResponses,
  generateLastTouchWarningText,
  getTickValues,
  getWhitelistedErrorMessage,
  processDruidTimeseriesData,
  takeTopNResults,
} from '../../support/utils';

export const TimeseriesErrorContainer = (props) => {
  return (
    <Container query={props.data.options.query} error={props.data.options.error}>
      <AnalyticsNoDataMessage analyticsType="timeseries" message={props.data.options.errorMessage} />
    </Container>
  );
};

export class Container extends React.PureComponent {
  render() {
    const { query, children, error } = this.props;

    const lastTouchWarningText = generateLastTouchWarningText(query.filter, query.data_sources);

    const lastTouchWarning = (
      <MessageBox
        text={lastTouchWarningText}
        type={'warning'}
        className={'analytics-display__message-box'}
        removeable
        noTitle
        hide={!lastTouchWarningText}
      />
    );

    const fullErrorMessageBox = error && (
      <MessageBox
        text={error}
        type={'error'}
        className={'analytics-display__message-box'}
        hide={!getWhitelistedErrorMessage({ error, whitelist: ERROR_WHITELIST })}
      />
    );

    return (
      <div className="analytics-display">
        {lastTouchWarning}
        {fullErrorMessageBox}
        {children}
      </div>
    );
  }
}

export class TimeseriesChart extends React.PureComponent {
  static propTypes = {
    data: PropTypes.object,
    query: PropTypes.object,
    unique: PropTypes.bool,
    product: PropTypes.string,
    localCurrency: PropTypes.string,
  };

  static defaultProps = {
    data: {},
    query: {},
    unique: false,
  };

  _getData = () => {
    const { data, additionalData } = this.props;
    if (!additionalData) {
      return data;
    }
    return Object.keys(additionalData).reduce((acc, event) => {
      const eventData = additionalData[event];
      if (!eventData) {
        return acc;
      }
      const flattenedReponses = flattenAllGroupByResponses(eventData);
      return {
        ...acc,
        [event]: flattenedReponses,
      };
    }, data || {});
  };

  render() {
    const { error, query, events, unique, limitReached, localCurrency } = this.props;
    const axesParameters: AxesParameters = { localCurrency };
    const data = this._getData();
    /**
     * check if an whitelisted substring of the full error message can be displayed.
     * The use case here is for errors procured from the backend analytics service (cenarius).
     */
    const errorMessageShort = getWhitelistedErrorMessage({
      error,
      whitelist: ERROR_WHITELIST,
    });

    /**
     * error message is one of the following:
     * 1. whitelisted substring of full error message
     * 2. full error message
     * 3. limit reached error message
     * 4. null (no error)
     */
    const errorMessage = errorMessageShort || error || (limitReached ? DATA_LIMIT_MESSAGE : null);

    // return early if error
    if (errorMessage) {
      return (
        <Container query={query} error={error}>
          <AnalyticsNoDataMessage analyticsType="timeseries" message={errorMessage} product={this.props.product} />
        </Container>
      );
    }

    // return early if data has not loaded
    if (!data || R.isEmpty(R.values(data))) {
      return (
        <Container query={query}>
          <BranchLoader />
        </Container>
      );
    }

    // /////////////////////////////////////////////////////////////////
    // / getting here means no error has occured and data has loaded ///
    // /////////////////////////////////////////////////////////////////

    const tickValues = getTickValues(data);
    const tickFormat = determineTickFormat(query.granularity);

    // returns an array of lineseries objects, one for each event
    const processedData = processDruidTimeseriesData(data, query.dimensions, events || query.data_sources, unique);

    let top6LineSeries = processedData.map((data) => takeTopNResults(Object.assign({}, data)));

    top6LineSeries = top6LineSeries.reduce((acc, series) => R.merge(acc, series), {});

    const multiAxesConfig = constructMultiAxesConfig(top6LineSeries, events, axesParameters);
    const yAxes = multiAxesConfig.map((config) => config.yAxis);
    const timeseriesData = multiAxesConfig.map((config) => config.data);

    if (!timeseriesData.length || timeseriesData.reduce((acc, partition) => acc + Object.keys(partition).length, 0) === 0) {
      return (
        <Container query={query}>
          <AnalyticsNoDataMessage analyticsType="timeseries" product={this.props.product} />
        </Container>
      );
    }

    const isLastTickToday = tickValues.length > 1 ? isToday(tickValues[tickValues.length - 1]) : false;

    return (
      <Container query={query}>
        <LineChart
          xAxis={{
            title: 'Date',
            tickFormat,
            tickValues,
          }}
          yAxis={yAxes}
          lines={timeseriesData}
          xType="time"
          includeDashedLine={isLastTickToday}
          includePointInSolidPart={isLastTickToday ? (d) => !isToday(d.x) : undefined}
          includePointInDashedPart={isLastTickToday ? (d) => isToday(d.x) || isYesterday(d.x) : undefined}
          granularity={query.granularity}
        />
      </Container>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  localCurrency: getCurrentAppLocalCurrency(state),
});

export default connect(mapStateToProps)(TimeseriesChart);
